import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { TranslateService } from './translate.service';
import { Questionnaire } from '../models/questionnaire';
import {
  QuestionnaireElement, QuestionnaireElementHeadline, QuestionnaireElementYesNo,
  QuestionnaireElementSlider, QuestionnaireElementSingleChoice, QuestionnaireElementTextDate,
  QuestionnaireElementMultipleChoice, QuestionnaireElementTextString,
  QuestionnaireElementTextArea
} from '../models/questionnaire-element';
import { Alarm } from '../models/alarm';
import { BasicError, Error403 } from '../models/error/error';

@Injectable()
export class PatientQuestionnaireService {
  private myQuestionnairesOfStudyUrl = '/api/v1/my/questionnaires';
  private myQuestionnairesDetailsURL = '/api/v1/questionnaires/';
  private myQuestionnaireStructureURL = '/api/v1/questionnaires/##id##/structure';
  private myQuestionnaireSubmitAnswersURL = '/api/v1/questionnaires/##id##/answersheets';
  private questionnairesOfStudyURL = '/api/v1/studies/##id##/questionnaires?limit=0';
  private evaluateAnswersheetURL = '/api/v1/answersheets/##id##/evaluate';

  constructor(private communication: CommunicationService, private translate: TranslateService) { }

  public getMyQuestionnairesOfStudy(currentPage: number = 1): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.myQuestionnairesOfStudyUrl + '?page=' + currentPage + '&limit=0').subscribe(json => {
      if (json instanceof BasicError) {
        console.log('----------------PatientQuestionnaireService----------: Error occured while reading questionnaires of study');

        if (json instanceof Error403) {
          console.log('----------------PatientQuestionnaireService----------: This user does' +
            ' not have the rights to read the questionnaires of this study');
        }

        retReplay.next(json);
        return;
      }

      // No Error occured, proceed with reading questionnaires
      const total_pages = json.meta.pagination.total_pages;
      const questionnaires = [];

      for (let i = 0; i < json.data.length; i++) {
        const questionnaireJSON = json.data[i];

        const quest = new Questionnaire(questionnaireJSON.id);
        if (questionnaireJSON.attributes.name !== undefined) { quest.setName(questionnaireJSON.attributes.name); };
        if (questionnaireJSON.attributes.title !== undefined) { quest.setTitle(questionnaireJSON.attributes.title); }
        if (questionnaireJSON.attributes.description !== undefined) { quest.setDescription(questionnaireJSON.attributes.description); }
        if (questionnaireJSON.attributes.origin !== undefined) { quest.setOrigin(questionnaireJSON.attributes.origin); }
        (questionnaireJSON.attributes.is_active !== undefined && questionnaireJSON.attributes.is_active === 1) ?
          quest.setActive(true) : quest.setActive(false);

        (questionnaireJSON.attributes.is_filled_out !== undefined && questionnaireJSON.attributes.is_filled_out) ?
          quest.setFilledOut(true) : quest.setFilledOut(false);

        questionnaires.push(quest);
      }

      if (total_pages === 1) {
        this.getMyQuestionnairesDetails(questionnaires).subscribe(jj => {
          if (jj instanceof BasicError) {
            console.log('----------------PatientQuestionnaireService----------: Failed to get '
              + 'Questionnaire details. Return this data instead.');
            retReplay.next(questionnaires);
            return;
          }
          retReplay.next(jj);
        });
      } else if (currentPage >= total_pages) {
        retReplay.next(questionnaires);
      } else if (currentPage === 1) {
        this.getMyQuestionnairesOfStudy((currentPage + 1)).subscribe(js => {
          if (js instanceof BasicError) {
            console.log('---------PatientQuestionnaireService-----: Failed to load following Questionnaires.');
            retReplay.next(questionnaires);
            return;
          }

          const va = questionnaires.concat(js);

          this.getMyQuestionnairesDetails(va).subscribe(jj => {
            if (jj instanceof BasicError) {
              console.log('----------------PatientQuestionnaireService----------: Failed to get '
                + 'Questionnaire details. Return this data instead.');
              retReplay.next(va);
              return;
            }
            retReplay.next(jj);
          });
        });
      } else {
        this.getMyQuestionnairesOfStudy((currentPage + 1)).subscribe(js => {
          if (js instanceof BasicError) {
            console.log('---------PatientQuestionnaireService-----: Failed to load following Questionnaires.');
            retReplay.next(questionnaires);
            return;
          }

          const va = questionnaires.concat(js);

          retReplay.next(va);
        });
      }
    });

    return retReplay;
  }

  /**
   * Getting detailed informations of questionnaires
   * @param questionnaires all my questionnaire objects
   */
  public getMyQuestionnairesDetails(questionnaires): Subject<any> {
    const retReplay = new Subject();

    let count = 0;
    const retQuest = [];
    for (let i = 0; i < questionnaires.length; i++) {
      const quest = questionnaires[i];

      this.getMyQuestionnaireDetail(quest).subscribe(json => {
        count = count + 1;
        if (json instanceof BasicError) {
          console.log('---------PatientQuestionnaireService-----: Failed to load Questionnaire detail');
          retQuest.push(quest);
        } else {
          retQuest.push(json);
        }

        if (count >= questionnaires.length) {
          retReplay.next(retQuest);
        }
      });
    }

    return retReplay;
  }

  /**
   * Getting detail of a specific questionnaire
   * @param quest Questionnaire for request
   */
  public getMyQuestionnaireDetail(quest: Questionnaire): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.myQuestionnairesDetailsURL + quest.getId()).subscribe(json => {
      if (json instanceof BasicError) {
        retReplay.next(json);
        return;
      }

      if (json.data.attributes.name !== undefined) { quest.setName(json.data.attributes.name); };
      if (json.data.attributes.title !== undefined) { quest.setTitle(json.data.attributes.title); }
      if (json.data.attributes.description !== undefined) { quest.setDescription(json.data.attributes.description); }
      if (json.data.attributes.origin !== undefined) { quest.setOrigin(json.data.attributes.origin); }
      (json.data.attributes.is_active !== undefined && json.data.attributes.is_active === 1) ?
        quest.setActive(true) : quest.setActive(false);
      if (json.data.attributes.can_use !== undefined) { quest.setCanUse(json.data.attributes.can_use) };

      (json.data.attributes.is_onetime !== undefined && json.data.attributes.is_onetime === 1) ?
        quest.setOneTime(true) : quest.setOneTime(false);

      (json.data.attributes.is_multiple !== undefined && json.data.attributes.is_multiple === 1) ?
        quest.setMultiple(true) : quest.setMultiple(false);

      (json.data.attributes.is_filled_out !== undefined &&
        (json.data.attributes.is_filled_out === 1 || json.data.attributes.is_filled_out === true)) ?
        quest.setFilledOut(true) : quest.setFilledOut(quest.isFilledOut());

      (json.data.attributes.is_schedule_changeable !== undefined && json.data.attributes.is_schedule_changeable === 1) ?
        quest.setScheduleChangeable(true) : quest.setScheduleChangeable(false);

      if (json.data.attributes.schedule !== undefined) { quest.setSchedule(json.data.attributes.schedule); }

      if (json.data.attributes.introtext !== undefined) { quest.setIntrotext(json.data.attributes.introtext); };

      if (json.data.attributes.outrotext !== undefined) { quest.setOutrotext(json.data.attributes.outrotext); }

      retReplay.next(quest);
    });

    return retReplay;
  }


  public getMyQuestionnaireStructure(quest: Questionnaire): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.myQuestionnaireStructureURL.replace('##id##', quest.getId().toString())).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---------PatientQuestionnaireService-----: Failed to load elements of study');
        retReplay.next(json);
        return;
      }

      const elements = [];

      for (let i = 0; i < json.data.length; i++) {
        const element = json.data[i];

        if (element.attributes.elementtype === 'elements/headlines') {
          // Is a Headline element
          const headerElem = new QuestionnaireElementHeadline();
          headerElem.setName(element.attributes.name);
          headerElem.setElementType(element.attributes.elementtype);
          headerElem.setPosition(i);

          headerElem.setHeadline(element.attributes.content.headline, 'patient');

          elements.push(headerElem);

        } else if (element.attributes.elementtype === 'elements/questions') {
          // Is an Question element
          let elem;
          switch (element.attributes.content.questiontype) {
            case 'YesNoSwitch':
              elem = new QuestionnaireElementYesNo();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);

              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            case 'Slider':
            case 'SAMScaleFace':
            case 'SAMScaleBody':
              elem = new QuestionnaireElementSlider();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);
              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            case 'SingleChoice':
              elem = new QuestionnaireElementSingleChoice();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);
              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            case 'TextDate':
              elem = new QuestionnaireElementTextDate();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);
              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            case 'MultipleChoice':
              elem = new QuestionnaireElementMultipleChoice();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);
              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            case 'TextString':
              elem = new QuestionnaireElementTextString();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);
              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            case 'TextArea':
              elem = new QuestionnaireElementTextArea();

              elem.setName(element.attributes.name);
              elem.setLabel(element.attributes.content.label);
              elem.setElementType(element.attributes.elementtype);
              (element.attributes.content.required) ? elem.setRequired(true) : elem.setRequired(false);
              if (element.attributes.content.values !== undefined && element.attributes.content.values !== null) {
                elem.setValues(element.attributes.content.values);
              } else {
                elem.setValues([]);
              }

              elem.setPosition(i);

              if (element.attributes.content.question !== undefined && element.attributes.content.question !== null) {
                elem.setQuestion(element.attributes.content.question, 'patient');
              } else {
                elem.setQuestion('', 'patient');
              }

              if (element.attributes.content.answers !== undefined && element.attributes.content.answers !== null) {
                elem.setAnswers(element.attributes.content.answers, 'patient');
              } else {
                elem.setAnswers(elem.getValues(), 'patient');
              }

              elements.push(elem);
              break;
            default:
              console.log('---------PatientQuestionnaireService-----: Element not recognized');
              console.log(element.attributes.content.questiontype);
          }
        } else {
          // Not recognized
          console.log('---------PatientQuestionnaireService-----: Element not recognized');
          console.log(element.attributes.elementtype);
        }
      }

      retReplay.next(elements);
    });

    return retReplay;
  }

  public submitAnswersOfQuestionnaire(answersId: String, answers = []): Subject<any> {
    const retReplay = new Subject();
    const date = new Date();
    const time = Math.floor((date.getTime() / 1000));

    for (const val of answers) {
      val['collected_at'] = time;
    }

    const payload = {
      'data': {
        'type': 'answersheets',
        'attributes': {
          'collected_at': time,
          'locale': this.translate.getSingleElementLanguage(),
          'answers': answers,
          'client': {
            'name': 'webapp',
            'device': 'browser',
            'os': 'computerandmobile'
          }
        }
      }
    };

    // Sending post http request to api
    this.communication.post(this.myQuestionnaireSubmitAnswersURL.replace('##id##', answersId.toString()), payload).subscribe(data => {
      if (data instanceof BasicError) {
        console.log('--------PatientQuestionnaireService---------: Failed to submit Answers.');
        console.log(data);
        retReplay.next(data);
      } else {
        // retReplay.next(true);
        // Instead of returning true, the answersheet id will be returned
        if (data.headers !== undefined && data.headers._headers.get('location') !== undefined) {
          const splits = data.headers._headers.get('location')[0].split('/');
          const id = splits[(splits.length - 1)];
          retReplay.next(id);
        } else {
          console.log('------------QuestionnaireService--------: No id found after submitting answers.');
          retReplay.next(null);
        }
      }
    });

    return retReplay;
  }

  /**
   * This function returns all questionnaires of a specified study
   * @param studyId studyid of study
   */
  public getQuestionnairesOfStudy(studyId: String): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.questionnairesOfStudyURL.replace('##id##', studyId.toString())).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----PatientQuestionnaireService-----: Could not load questionnaire of study');
        retReplay.next(json);
        return;
      }

      // Got questionnaires of study
      const questionnaires = [];

      for (let i = 0; i < json.data.length; i++) {
        const questionnaireJSON = json.data[i];

        const quest = new Questionnaire(questionnaireJSON.id);
        if (questionnaireJSON.attributes.name !== undefined) { quest.setName(questionnaireJSON.attributes.name); };
        if (questionnaireJSON.attributes.title !== undefined) { quest.setTitle(questionnaireJSON.attributes.title); }
        if (questionnaireJSON.attributes.description !== undefined) { quest.setDescription(questionnaireJSON.attributes.description); }
        if (questionnaireJSON.attributes.origin !== undefined) { quest.setOrigin(questionnaireJSON.attributes.origin); }
        (questionnaireJSON.attributes.is_active !== undefined && questionnaireJSON.attributes.is_active === 1) ?
          quest.setActive(true) : quest.setActive(false);

        (questionnaireJSON.attributes.is_filled_out !== undefined && questionnaireJSON.attributes.is_filled_out) ?
          quest.setFilledOut(true) : quest.setFilledOut(false);

        questionnaires.push(quest);
      }

      retReplay.next(questionnaires);
    });

    return retReplay;
  }

  /**
   * Evaluating answersheet of user
   * @param answersheetId answersheet to evaluate
   */
  public evaluateAnswersheet(answersheetId): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.evaluateAnswersheetURL.replace('##id##', answersheetId)).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----------PatientQuestionnaireService-----: Evaluation of answersheet went wrong.');
        retReplay.next(json);
        return;
      }

      // Get feedbacks if some exists
      const id = json.data.id;
      const answerId = json.data.attributes.answersheet_id;
      const collectedAt = json.data.attributes.collected_at;
      const feedbacks = [];

      for (const feedback of json.data.attributes.feedbacks) {
        // Get feedback
        const feedId = feedback.id;
        const result = feedback.result;
        const rule = feedback.rule;
        const headline = feedback.true.headline;
        const text = feedback.true.text;
        const explanation = feedback.true.explanation;
        const cause = feedback.true.cause;
        let isPrivate = 0;
        if (feedback.is_private !== undefined) { isPrivate = feedback.is_private; }

        // Create Feedback
        const f = new Alarm(feedId, answerId);
        (isPrivate !== 0) ? f.setAlarm(result) : f.setWarning(result);
        f.setCollectedAt(collectedAt);
        f.setResult(result);
        f.setRule(rule);
        f.setHeadline(headline);
        f.setText(text);
        f.setExplanation(explanation);
        f.setCause(cause);

        feedbacks.push(f);
      }

      retReplay.next(feedbacks);
    });

    return retReplay;
  }
}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/patient-questionnaire.service.ts